import {
  connectRange
} from 'instantsearch.js/es/connectors'

import { isRoomTypePage } from '../urlHelpers'
import { t } from './translations'
import { isEur, gbpToDisplayOrAnchor, displayToGbp } from './currency'

const periodSort = (a, b) => {
  if (a.name === 'Unknown') return 1
  if (b.name === 'Unknown') return -1

  return b.name.localeCompare(a.name)
}

const sortStringsAsNumbers = (firstNum, secondNum) => {
  return firstNum.name.localeCompare(secondNum.name, undefined, { numeric: true, sensitivity: 'base' })
}

// Facet values come back in the current locale (e.g. "Petit" on fr-fr, not
// "Small"), so the size order has to be translated too rather than matched
// against hardcoded English names — otherwise every lookup below misses on
// non-English PLPs. The cm ranges themselves aren't language-specific, so
// they're matched to a translated name by position, not by name.
const ART_SIZE_ORDER_FALLBACK = ['Miniature', 'Small', 'Medium', 'Large', 'X-Large']
const artSizeOrder = () => {
  const translated = t('art_size_order')
  return Array.isArray(translated) ? translated : ART_SIZE_ORDER_FALLBACK
}
const ART_SIZE_RANGES = ['<30cm', '30-50cm', '50-80cm', '80-100cm', '>100cm']

const sortArtSizes = (firstSize, secondSize) => {
  const order = artSizeOrder()
  return order.indexOf(firstSize.name) - order.indexOf(secondSize.name)
}

const withArtSizeRange = (item) => {
  const range = ART_SIZE_RANGES[artSizeOrder().indexOf(item.label)] || ''
  const label = `${item.label} ${range}`.trim()
  return { ...item, label, highlighted: label }
}

const modalOrList = (isShowingMore, filterName) => isShowingMore ? t('show_less') : `<span class="show-more-modal" id="show_more_${filterName}" data-facet="${filterName}">${t('show_more')}</span>`

function categoryHierarchyAttributes () {
  return [
    'category_hierarchy.lvl0',
    'category_hierarchy.lvl1',
    'category_hierarchy.lvl2',
    'category_hierarchy.lvl3'
  ]
}

const excludedAttributesForCurrentRefinements = () => {
  const nonCategorySearchPaths = ['/search', '/collections', '/sellers']
  const otherAttributes = [
    'user_slug',
    'query',
    'commercially_accessible',
    'luxury_items'
  ]

  if (nonCategorySearchPaths.some(path => window.location.pathname.includes(path))) {
    return otherAttributes
  } else if (window.location.pathname.includes('/sale')) {
    return otherAttributes.concat(['sale'])
  } else if (window.location.pathname.includes('/new')) {
    return otherAttributes.concat(['published_recently'])
  } else if (isRoomTypePage()) {
    return otherAttributes.concat(['category_hierarchy.lvl0', 'most_specific_category_name'])
  } else {
    return categoryHierarchyAttributes().concat(otherAttributes)
  }
}

// The legacy hierarchical attributes are excluded from the page pills: with the
// single-select tree a category click navigates to its own SEO URL, so a
// dismissible pill can't represent it. Multi-select ticks (category_paths,
// behind :plp_multi_select_categories) ARE pills — they're refinements like any
// other filter; only the page's own scope is stripped, via
// withoutLandingScopeCategoryRefinements.
function excludedAttributesForPageCurrentRefinements () {
  return excludedAttributesForCurrentRefinements().concat(categoryHierarchyAttributes())
}

const excludedAttributesForClearRefinements = () => {
  const nonCategorySearchPaths = ['/search', '/collections']

  if (nonCategorySearchPaths.some(path => window.location.pathname.includes(path))) {
    return ['query', 'commercially_accessible', 'luxury_items']
  } else if (window.location.pathname.includes('/sale')) {
    return ['query', 'sale']
  } else if (window.location.pathname.includes('/new')) {
    return ['query', 'published_recently', 'commercially_accessible', 'luxury_items']
  } else if (isRoomTypePage()) {
    // category_paths matches the category-page branch below: selections
    // survive "Clear filters" on room-type pages too
    return ['query', 'category_hierarchy.lvl0', 'most_specific_category_name', 'category_paths']
  } else if (window.location.pathname.includes('/seller')) {
    return ['user_slug']
  } else {
    // category selections survive "Clear filters" on category pages, matching
    // the single-select behaviour where the category is the page, not a filter
    return categoryHierarchyAttributes().concat(['category_paths'])
  }
}

const setSelectedCategoryCheckmark = () => {
  const selected = document.querySelectorAll('.ais-HierarchicalMenu-item--selected')
  if (selected.length === 0) return
  selected.forEach(item => {
    item.classList.remove('selected-category')
  })
  const lastSelected = selected[selected.length - 1]
  lastSelected.classList.add('selected-category')
}

const customizeSearchInputs = () => {
  const minInputClass = 'ais-RangeInput-input--min'
  const maxInputClass = 'ais-RangeInput-input--max'
  const priceContainerIds = [
    'price',
    'mobile-price'
  ]
  const dimensionContainerIds = [
    'dimensions-container',
    'mobile-dimensions-root'
  ]

  priceContainerIds.forEach((containerId) => {
    const container = document.getElementById(containerId)
    if (!container) return

    const minElements = Array.from(container.getElementsByClassName(minInputClass))
    minElements.forEach((element) => {
      element.placeholder = '50'
    })

    const maxElements = Array.from(container.getElementsByClassName(maxInputClass))
    maxElements.forEach((element) => {
      element.placeholder = '50000'
    })
  })

  dimensionContainerIds.forEach((containerId) => {
    const container = document.getElementById(containerId)
    if (!container) return

    const minElements = Array.from(container.getElementsByClassName(minInputClass))
    minElements.forEach((element) => {
      element.placeholder = '0'
    })

    const maxElements = Array.from(container.getElementsByClassName(maxInputClass))
    maxElements.forEach((element) => {
      element.placeholder = '500'
    })
  })

  setRangeLabelCssVars()
}

const setRangeLabelCssVars = () => {
  // Set CSS custom properties on containers so ::before pseudo-elements
  // can display localised labels without injecting into Preact-managed DOM
  const dimensionIds = ['width', 'height', 'depth']
  dimensionIds.forEach(id => {
    const el = document.getElementById(id)
    if (el) {
      el.style.setProperty('--range-min-label', `'${t('range_min_cm')}'`)
      el.style.setProperty('--range-max-label', `'${t('range_max_cm')}'`)
    }
  })

  const priceEl = document.getElementById('price')
  if (priceEl) {
    priceEl.style.setProperty('--range-min-label', `'${t('range_min_price')}'`)
    priceEl.style.setProperty('--range-max-label', `'${t('range_max_price')}'`)
  }

  // Mobile dimension title labels
  const mobileDims = [
    { id: 'mobile-width', label: t('filter_width') },
    { id: 'mobile-height', label: t('filter_height') },
    { id: 'mobile-depth', label: t('filter_depth') }
  ]
  mobileDims.forEach(({ id, label }) => {
    const el = document.getElementById(id)
    if (el) {
      el.style.setProperty('--dimension-title', `'${label}'`)
    }
  })
}

function initializeDimensionsApplyButton () {
  const applyButton = document.getElementById('vjs-apply-dimensions')
  if (!applyButton) return

  applyButton.addEventListener('click', () => {
    const forms = document.querySelectorAll('#width form, #height form, #depth form')
    forms.forEach(form => form.requestSubmit())
  })
}

function initializePriceApplyButton () {
  const applyButton = document.getElementById('vjs-apply-price')
  if (!applyButton) return

  applyButton.addEventListener('click', () => {
    const form = document.querySelector('#price form')
    if (form) form.requestSubmit()
  })
}

// Value-hiding here is mirrored per attribute by selectableFacetValues in
// src/algolia_search/selectAllFilters.js — keep the two in sync
const removeUnknowns = (items) => items.filter(item => item.value.toLowerCase() !== 'unknown')

const removeSeatersMoreThanFour = (items) => items.filter(item => item.value <= 4)

const noOfSeatersOrFour = (item) => {
  if (item.value >= 4) {
    return {
      ...item,
      value: 4,
      label: t('four_seater_and_more'),
      highlighted: t('four_seater_and_more')
    }
  } else {
    return {
      ...item,
      label: item.label + t('seater_suffix'),
      highlighted: item.label + t('seater_suffix')
    }
  }
}

const buildPriceRangeForm = (container, widgetParams, refine) => {
  const wrapper = document.createElement('div')
  wrapper.className = ['ais-RangeInput', 'ais-RangeInput--noRefinement', widgetParams.cssClasses?.root]
    .filter(Boolean)
    .join(' ')

  const form = document.createElement('form')
  form.className = 'ais-RangeInput-form'

  form.addEventListener('submit', event => {
    event.preventDefault()

    const [minInput, maxInput] = event.target.elements

    const rawMinInputValue = parseFloat(minInput.value)
    const rawMaxInputValue = parseFloat(maxInput.value)

    refine([
      Number.isFinite(rawMinInputValue) ? displayToGbp(rawMinInputValue) : undefined,
      Number.isFinite(rawMaxInputValue) ? displayToGbp(rawMaxInputValue) : undefined
    ])
  })

  wrapper.appendChild(form)
  container.appendChild(wrapper)

  return form
}

// Custom range widget so we can change prices behind curtains before sending them to algolia
// and are able to fix the display of the refinement on front end when we get results back
// We did this at one point because of service fee, but not anymore
const renderCustomRangeInput = (renderOptions) => {
  const { start, range, refine, widgetParams } = renderOptions
  const [min, max] = [Math.ceil(start[0]), Math.floor(start[1])]

  // widgetParams.container e.g. price, mobile-price
  const container = document.querySelector(widgetParams.container)

  // (Re)built whenever missing, not just on first render: the container can be
  // emptied between renders (e.g. a Turbo visit replacing the page body)
  const form = container.querySelector('form') || buildPriceRangeForm(container, widgetParams, refine)

  const minPlaceholder = t(isEur() ? 'price_min_placeholder_eur' : 'price_min_placeholder')
  const maxPlaceholder = t(isEur() ? 'price_max_placeholder_eur' : 'price_max_placeholder')
  // A value equal to a fixed bound is the widget's own bound, not a shopper's
  // number - blank, as the stock rangeInput renders it
  const displayMin = Number.isFinite(min) && min !== range.min ? gbpToDisplayOrAnchor(min) : ''
  const displayMax = Number.isFinite(max) && max !== range.max ? gbpToDisplayOrAnchor(max) : ''

  form.innerHTML = `
    <label class="ais-RangeInput-label">
      <input
        class="ais-RangeInput-input ais-RangeInput-input--min"
        type="number"
        min="-1"
        max="690000"
        step="1" placeholder="${minPlaceholder}"
        value="${displayMin}"
      >
    </label>
    <span class="ais-RangeInput-separator">${t('price_separator')}</span>

    <label class="ais-RangeInput-label">
      <input
        class="ais-RangeInput-input ais-RangeInput-input--max"
        min="-1"
        max="690000"
        step="1"
        placeholder="${maxPlaceholder}"
        type="number"
        value="${displayMax}"
      >
    </label>
    <button type="submit" class="ais-RangeInput-submit">${t('apply')}</button>
  `
}

const customPriceRangeWidget = connectRange(renderCustomRangeInput)

export {
  periodSort,
  sortStringsAsNumbers,
  sortArtSizes,
  withArtSizeRange,
  excludedAttributesForCurrentRefinements,
  excludedAttributesForPageCurrentRefinements,
  excludedAttributesForClearRefinements,
  customizeSearchInputs,
  initializeDimensionsApplyButton,
  initializePriceApplyButton,
  setSelectedCategoryCheckmark,
  removeUnknowns,
  modalOrList,
  noOfSeatersOrFour,
  removeSeatersMoreThanFour,
  customPriceRangeWidget,
  renderCustomRangeInput
}
